/**
 * Observability Manager
 *
 * Central manager for telemetry and monitoring throughout the application.
 * This manager provides a unified interface for capturing events, errors, and metrics
 * while remaining agnostic to the underlying implementation.
 *
 * Everything handed to the provider is scrubbed first (see ./scrub): query
 * strings removed, sensitive keys redacted, users reduced to their id. The
 * scrub always runs; `beforeCapture` can only filter further.
 */

import { NoOpObservabilityProvider } from './providers/no-op.provider';
import { scrubRecord, scrubUser, stripQueryStrings } from './scrub';
import {
  DEFAULT_OBSERVABILITY_CONFIG,
  ObservabilitySeverity,
} from '../../types/observability';
import { dayjs } from '../../utils/date';

import type {
  ObservabilityProvider,
  ObservabilityConfig,
  ObservabilityEvent,
  ObservabilityError,
  ObservabilityMetric,
  ObservabilityUser,
  ObservabilityBreadcrumb,
} from '../../types/observability';

export class ObservabilityManager {
  private config: ObservabilityConfig;
  private provider: ObservabilityProvider;
  private defaultTags: Record<string, string> = {};

  constructor(config: Partial<ObservabilityConfig> = {}) {
    this.config = { ...DEFAULT_OBSERVABILITY_CONFIG, ...config };
    this.provider = this.config.provider || new NoOpObservabilityProvider();
    this.defaultTags = scrubRecord(this.config.defaultTags) ?? {};

    // Apply default tags
    Object.entries(this.defaultTags).forEach(([key, value]) => {
      this.provider.setTag(key, value);
    });
  }

  /**
   * Sets the observability provider
   * This allows late binding of the provider after manager creation
   */
  setProvider(provider: ObservabilityProvider): void {
    this.provider = provider;

    // Reapply default tags to new provider
    Object.entries(this.defaultTags).forEach(([key, value]) => {
      this.provider.setTag(key, value);
    });
  }

  /**
   * Merges config into this manager in place: the provider (when given), the
   * default tags and the remaining options. Callers that already hold this
   * manager see the change, which a replacement manager could not give them.
   */
  configure(config: Partial<ObservabilityConfig>): void {
    const { provider, defaultTags, ...rest } = config;
    this.config = { ...this.config, ...rest };
    if (defaultTags) {
      this.defaultTags = {
        ...this.defaultTags,
        ...scrubRecord(defaultTags),
      };
    }
    this.setProvider(provider ?? this.provider);
  }

  /**
   * Enables or disables observability
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Checks if observability is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Whether failed requests answered with a 4xx should be reported
   */
  isReportingClientErrors(): boolean {
    return this.config.reportClientErrors ?? true;
  }

  /**
   * Captures a message event
   */
  captureMessage(
    message: string,
    severity: ObservabilitySeverity,
    context?: Record<string, unknown>,
    tags?: Record<string, string>
  ): void {
    if (!this.config.enabled) return;

    const event: ObservabilityEvent = {
      message: stripQueryStrings(message),
      severity,
      timestamp: dayjs().valueOf(),
      context: scrubRecord(context),
      tags: scrubRecord({ ...this.defaultTags, ...tags }),
    };

    // Allow beforeCapture hook to prevent capture
    if (this.config.beforeCapture && !this.config.beforeCapture(event)) {
      return;
    }

    this.provider.captureMessage(event);
  }

  /**
   * Captures an error
   */
  captureError(
    error: Error,
    severity: ObservabilitySeverity,
    context?: Record<string, unknown>,
    tags?: Record<string, string>
  ): void {
    if (!this.config.enabled) return;

    const errorEvent: ObservabilityError = {
      error,
      severity,
      context: scrubRecord(context),
      tags: scrubRecord({ ...this.defaultTags, ...tags }),
    };

    // Allow beforeCapture hook to prevent capture
    if (this.config.beforeCapture && !this.config.beforeCapture(errorEvent)) {
      return;
    }

    this.provider.captureError(errorEvent);
  }

  /**
   * Records a custom metric
   */
  recordMetric(
    name: string,
    value: number,
    unit?: 'milliseconds' | 'bytes' | 'count' | 'percentage',
    tags?: Record<string, string>
  ): void {
    if (!this.config.enabled) return;

    const metric: ObservabilityMetric = {
      name,
      value,
      unit,
      tags: scrubRecord({ ...this.defaultTags, ...tags }),
    };

    this.provider.recordMetric(metric);
  }

  /**
   * Sets the current user
   */
  setUser(user: ObservabilityUser | null): void {
    if (!this.config.enabled) return;
    this.provider.setUser(scrubUser(user));
  }

  /**
   * Adds a breadcrumb
   */
  addBreadcrumb(
    message: string,
    category?: string,
    level?: ObservabilitySeverity,
    data?: Record<string, unknown>
  ): void {
    if (!this.config.enabled) return;

    const breadcrumb: ObservabilityBreadcrumb = {
      message: stripQueryStrings(message),
      category,
      level,
      timestamp: dayjs().valueOf(),
      data: scrubRecord(data),
    };

    this.provider.addBreadcrumb(breadcrumb);
  }

  /**
   * Sets a custom tag
   */
  setTag(key: string, value: string): void {
    if (!this.config.enabled) return;
    const scrubbed = scrubRecord({ [key]: value })?.[key] ?? value;
    this.defaultTags[key] = scrubbed;
    this.provider.setTag(key, scrubbed);
  }

  /**
   * Sets custom context
   */
  setContext(name: string, context: Record<string, unknown>): void {
    if (!this.config.enabled) return;
    this.provider.setContext(name, scrubRecord(context) ?? {});
  }

  /**
   * Clears all context
   */
  clearContext(): void {
    if (!this.config.enabled) return;
    this.provider.clearContext();
  }

  /**
   * Convenience method: Capture info message
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.captureMessage(message, ObservabilitySeverity.INFO, context);
  }

  /**
   * Convenience method: Capture warning
   */
  warning(message: string, context?: Record<string, unknown>): void {
    this.captureMessage(message, ObservabilitySeverity.WARNING, context);
  }

  /**
   * Convenience method: Capture error message
   */
  error(message: string, context?: Record<string, unknown>): void {
    this.captureMessage(message, ObservabilitySeverity.ERROR, context);
  }

  /**
   * Convenience method: Capture exception
   */
  exception(error: Error, context?: Record<string, unknown>): void {
    this.captureError(error, ObservabilitySeverity.ERROR, context);
  }
}
